<template>
  <section class="hero-carousel" aria-label="Pacis products">
    <v-carousel
      cycle
      :interval="7000"
      :show-arrows="slides.length > 1"
      hide-delimiter-background
      height="280"
      class="hero-carousel__track"
    >
      <v-carousel-item
        v-for="slide in slides"
        :key="slide.id"
      >
        <div
          class="hero-slide"
          :style="{ backgroundImage: `url(${slide.image})` }"
        >
          <div class="hero-slide__overlay" />
          <div class="hero-slide__content">
            <p v-if="greeting" class="hero-slide__greet">{{ greeting }}</p>
            <p class="hero-slide__eyebrow">{{ slide.eyebrow }}</p>
            <h2 class="hero-slide__title">{{ slide.title }}</h2>
            <p class="hero-slide__subtitle">{{ slide.subtitle }}</p>
            <router-link
              v-if="slide.route"
              :to="slide.route"
              class="hero-slide__cta"
            >
              {{ slide.ctaLabel }}
              <v-icon small color="#1E4276">mdi-arrow-right</v-icon>
            </router-link>
          </div>
        </div>
      </v-carousel-item>
    </v-carousel>
  </section>
</template>

<script>
import { buildDashboardHeroSlides } from '@/constants/dashboardHeroSlides';

export default {
  name: 'DashboardHeroCarousel',
  props: {
    greeting: { type: String, default: '' },
  },
  computed: {
    slides() {
      return buildDashboardHeroSlides();
    },
  },
};
</script>

<style scoped>
.hero-carousel {
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 8px 28px rgba(15, 45, 82, 0.12);
}

.hero-carousel__track {
  border-radius: 16px;
}

.hero-slide {
  position: relative;
  height: 100%;
  min-height: 280px;
  background-size: cover;
  background-position: center right;
  background-color: #0f2d52;
  display: flex;
  align-items: flex-end;
}

.hero-slide__overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    105deg,
    rgba(15, 45, 82, 0.92) 0%,
    rgba(24, 70, 121, 0.78) 42%,
    rgba(24, 70, 121, 0.25) 72%,
    rgba(24, 70, 121, 0.08) 100%
  );
}

.hero-slide__content {
  position: relative;
  z-index: 1;
  padding: 24px 20px 56px;
  max-width: 560px;
  width: 100%;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .hero-slide__content {
    padding: 36px 32px 56px;
  }
}

.hero-slide__greet {
  margin: 0 0 10px;
  font-size: 1.2rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.hero-slide__eyebrow {
  margin: 0 0 6px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #F5C518;
}

.hero-slide__title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

@media (min-width: 768px) {
  .hero-slide__greet { font-size: 1.35rem; }
  .hero-slide__title { font-size: 1.65rem; }
}

.hero-slide__subtitle {
  margin: 0 0 16px;
  font-size: 0.85rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
  max-width: 420px;
}

.hero-slide__cta {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 999px;
  background: #F5C518;
  color: #1E4276;
  font-size: 0.82rem;
  font-weight: 800;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  pointer-events: auto;
}

.hero-slide__cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(245, 197, 24, 0.45);
}

/* Keep Vuetify arrows/dots above slide content so carousel can move */
.hero-carousel >>> .v-carousel__controls {
  z-index: 1;
  padding-bottom: 8px;
  pointer-events: auto;
}

.hero-carousel >>> .v-window__prev,
.hero-carousel >>> .v-window__next {
  z-index: 1;
  pointer-events: auto;
}

.hero-carousel >>> .v-btn--icon.v-size--small {
  color: rgba(255, 255, 255, 0.85);
}

.hero-carousel >>> .v-carousel__controls__item .v-icon {
  opacity: 0.5;
}

.hero-carousel >>> .v-carousel__controls__item.v-item--active .v-icon {
  opacity: 1;
  color: #F5C518;
}

@media (max-width: 600px) {
  .hero-slide__greet { font-size: 1.05rem; }
  .hero-slide__title { font-size: 1.15rem; }
  .hero-slide__subtitle {
    font-size: 0.8rem;
    margin-bottom: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hero-slide__content { padding: 20px 16px 52px; }
}
</style>
